import { ApiError, type ApiErrorCode, type ApiFieldError } from './errors'

/**
 * The in-house fetch wrapper.
 *
 * There is no axios in this repo, and this file is the reason. What axios
 * would have bought us is interceptors and a timeout; what it costs is a
 * dependency, its transitive tree, and a second HTTP mental model alongside
 * `fetch`. Everything below is the *specific* behaviour this API needs:
 *
 *   - the `{success, data, error, meta}` envelope is unwrapped once, here, so
 *     no screen ever writes `response.data.data`;
 *   - failures throw a typed `ApiError` carrying the machine `code`, because
 *     switching on a message is a bug waiting for a copy edit;
 *   - the bearer token comes from a pluggable provider, so auth state lives in
 *     the app's store and not in a module-level variable this package owns;
 *   - 401 fires a callback and nothing else — this file does not know what a
 *     router is, and a design-system primitive that can navigate is a
 *     primitive that cannot be unit tested;
 *   - every request carries a timeout, because an island link that goes quiet
 *     does not close the socket, it just stops answering, and a `fetch` with
 *     no timeout hangs until the tab is closed.
 */

// ---------------------------------------------------------------------------
// Envelope
// ---------------------------------------------------------------------------

export interface ApiMeta {
  next_cursor?: string | null
  has_more?: boolean
  count?: number
  request_id?: string
}

export interface ApiEnvelope<T> {
  success: boolean
  data?: T
  error?: {
    code: ApiErrorCode
    message: string
    fields?: ApiFieldError[]
    details?: Record<string, unknown>
  }
  meta?: ApiMeta
}

/** A page of results plus the cursor that continues it. */
export interface ApiPage<T> {
  items: T[]
  meta: ApiMeta
}

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export type TokenProvider = () => string | null | undefined | Promise<string | null | undefined>

export interface ApiClientOptions {
  /** e.g. `/backoffice/v1` or `https://fish-api.example.com/portal/v1`. */
  baseUrl: string
  /**
   * Supplies the bearer token. Pluggable rather than a setter so the client
   * always reads the *current* token: a stale closure over a token that has
   * since been refreshed is the classic cause of a random 401 mid-session.
   */
  getToken?: TokenProvider
  /**
   * Called on any 401. The app decides what that means — clear the session and
   * route to login, or attempt a refresh and let the caller retry. Returning
   * `true` tells the wrapper the credential was repaired and the request is
   * retried exactly once.
   */
  onUnauthorized?: (error: ApiError) => boolean | void | Promise<boolean | void>
  /** Milliseconds before a request is aborted. Default 15000. */
  timeoutMs?: number
  /** Extra headers on every request (locale, tenant, feature flags). */
  headers?: Record<string, string> | (() => Record<string, string>)
  /** Injected for tests. Defaults to the global `fetch`. */
  fetch?: typeof globalThis.fetch
  /**
   * Retry policy for requests the wrapper considers safe to repeat. Only GET
   * and HEAD are retried automatically; a POST is never retried without an
   * `Idempotency-Key`, and deciding that is the caller's job, not ours.
   */
  retry?: { attempts?: number; baseDelayMs?: number }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD'
  /** Query string parameters. `undefined` and `null` values are dropped. */
  query?: Record<string, string | number | boolean | null | undefined>
  /** JSON request body. Serialised here; do not pre-stringify. */
  body?: unknown
  headers?: Record<string, string>
  /** Caller's cancellation signal. Combined with the timeout's own signal. */
  signal?: AbortSignal
  /** Overrides the client default for this call. `0` disables the timeout. */
  timeoutMs?: number
  /**
   * Required by the API on every partner write and every device-ingested
   * write. Passing the same key with the same body replays the original
   * response; with a different body the server returns `idempotency_key_reused`.
   */
  idempotencyKey?: string
  /** Overrides the retry policy for this call. */
  retry?: { attempts?: number; baseDelayMs?: number }
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export interface ApiClient {
  /** Issues a request and returns the unwrapped `data`. */
  request: <T>(path: string, options?: RequestOptions) => Promise<T>
  /** As `request`, but also returns `meta` — needed for cursor pagination. */
  requestPage: <T>(path: string, options?: RequestOptions) => Promise<ApiPage<T>>
  /** Fetches a binary payload (a bill PDF, a CSV export) with auth attached. */
  requestBlob: (path: string, options?: RequestOptions) => Promise<Blob>
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) => Promise<T>
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) => Promise<T>
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) => Promise<T>
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) => Promise<T>
  del: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) => Promise<T>
  /** Absolute URL for a path — for `<a href>` and `EventSource`. */
  resolve: (path: string, query?: RequestOptions['query']) => string
  readonly options: Readonly<ApiClientOptions>
}

const RETRYABLE_STATUSES = new Set([429, 502, 503, 504])

export function createApiClient(options: ApiClientOptions): ApiClient {
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis)
  const defaultTimeout = options.timeoutMs ?? 15_000
  const defaultRetry = { attempts: options.retry?.attempts ?? 2, baseDelayMs: options.retry?.baseDelayMs ?? 300 }

  const resolve = (path: string, query?: RequestOptions['query']): string => {
    const base = options.baseUrl.replace(/\/+$/, '')
    const suffix = path.startsWith('/') ? path : `/${path}`
    const search = buildQuery(query)
    return base + suffix + search
  }

  async function send<T>(
    path: string,
    request: RequestOptions,
    parse: 'json' | 'blob',
    allowUnauthorizedRetry: boolean,
  ): Promise<{ data: T; meta: ApiMeta; blob?: Blob }> {
    const method = request.method ?? 'GET'
    const url = resolve(path, request.query)

    const headers = new Headers()
    headers.set('Accept', parse === 'blob' ? '*/*' : 'application/json')
    for (const [key, value] of Object.entries(resolveHeaders(options.headers))) headers.set(key, value)
    if (request.body !== undefined) headers.set('Content-Type', 'application/json')
    if (request.idempotencyKey) headers.set('Idempotency-Key', request.idempotencyKey)
    // Client-generated so a failed request that never reached a log still has
    // an id the user can quote; the server echoes it back in `meta.request_id`.
    headers.set('X-Request-Id', newRequestId())
    for (const [key, value] of Object.entries(request.headers ?? {})) headers.set(key, value)

    const token = await options.getToken?.()
    if (token) headers.set('Authorization', `Bearer ${token}`)

    const timeoutMs = request.timeoutMs ?? defaultTimeout
    const controller = new AbortController()
    const timer =
      timeoutMs > 0
        ? setTimeout(() => controller.abort(new DOMException('timeout', 'TimeoutError')), timeoutMs)
        : undefined
    const signal = combineSignals(controller.signal, request.signal)

    let response: Response
    try {
      response = await doFetch(url, {
        method,
        headers,
        signal,
        ...(request.body !== undefined ? { body: JSON.stringify(request.body) } : {}),
      })
    } catch (cause) {
      throw abortOrNetworkError(cause, request.signal, url)
    } finally {
      if (timer !== undefined) clearTimeout(timer)
    }

    const requestId = response.headers.get('X-Request-Id') ?? undefined

    if (parse === 'blob') {
      if (!response.ok) throw await errorFromResponse(response, requestId)
      return { data: undefined as T, meta: {}, blob: await response.blob() }
    }

    // 204 with an empty body is a legitimate success for a DELETE.
    if (response.status === 204 || response.headers.get('Content-Length') === '0') {
      if (!response.ok) throw await errorFromResponse(response, requestId)
      return { data: undefined as T, meta: {} }
    }

    let envelope: ApiEnvelope<T>
    try {
      envelope = (await response.json()) as ApiEnvelope<T>
    } catch (cause) {
      throw new ApiError({
        code: 'malformed_response',
        message: `The server returned a response that is not JSON (HTTP ${response.status}).`,
        status: response.status,
        ...(requestId !== undefined ? { requestId } : {}),
        cause,
      })
    }

    const meta = envelope.meta ?? {}
    const effectiveRequestId = meta.request_id ?? requestId

    if (!response.ok || envelope.success === false) {
      const error = new ApiError({
        code: envelope.error?.code ?? statusToCode(response.status),
        message: envelope.error?.message ?? `Request failed with HTTP ${response.status}.`,
        status: response.status,
        ...(envelope.error?.fields !== undefined ? { fields: envelope.error.fields } : {}),
        ...(envelope.error?.details !== undefined ? { details: envelope.error.details } : {}),
        ...(effectiveRequestId !== undefined ? { requestId: effectiveRequestId } : {}),
      })

      if (response.status === 401 && allowUnauthorizedRetry && options.onUnauthorized) {
        const repaired = await options.onUnauthorized(error)
        if (repaired === true) return send<T>(path, request, parse, false)
      }
      throw error
    }

    return { data: envelope.data as T, meta }
  }

  /** Wraps `send` with the retry policy for safe methods. */
  async function withRetry<T>(
    path: string,
    request: RequestOptions,
    parse: 'json' | 'blob',
  ): Promise<{ data: T; meta: ApiMeta; blob?: Blob }> {
    const method = request.method ?? 'GET'
    const idempotent = method === 'GET' || method === 'HEAD' || Boolean(request.idempotencyKey)
    const policy = { ...defaultRetry, ...request.retry }
    const attempts = idempotent ? Math.max(1, policy.attempts) : 1

    let lastError: unknown
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      try {
        return await send<T>(path, request, parse, true)
      } catch (error) {
        lastError = error
        const retryable =
          error instanceof ApiError &&
          !error.isAborted &&
          (error.isNetwork || RETRYABLE_STATUSES.has(error.status))
        if (!retryable || attempt === attempts - 1) throw error
        // Exponential backoff with jitter. Without jitter, fifty tablets that
        // lost the same tower all come back in the same millisecond.
        const delay = policy.baseDelayMs * 2 ** attempt
        await sleep(delay + Math.random() * policy.baseDelayMs)
      }
    }
    throw lastError
  }

  const client: ApiClient = {
    options,
    resolve,
    request: async <T>(path: string, request: RequestOptions = {}) =>
      (await withRetry<T>(path, request, 'json')).data,
    requestPage: async <T>(path: string, request: RequestOptions = {}) => {
      const result = await withRetry<T[] | { items?: T[] }>(path, request, 'json')
      return { items: normaliseItems<T>(result.data), meta: result.meta }
    },
    requestBlob: async (path: string, request: RequestOptions = {}) => {
      const result = await withRetry<unknown>(path, request, 'blob')
      return result.blob ?? new Blob()
    },
    get: (path, request = {}) => client.request(path, { ...request, method: 'GET' }),
    post: (path, body, request = {}) => client.request(path, { ...request, method: 'POST', body }),
    put: (path, body, request = {}) => client.request(path, { ...request, method: 'PUT', body }),
    patch: (path, body, request = {}) => client.request(path, { ...request, method: 'PATCH', body }),
    del: (path, request = {}) => client.request(path, { ...request, method: 'DELETE' }),
  }

  return client
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Collection endpoints return `data` as an array. A couple return an object
 * with an `items` array because they carry sibling aggregates. Both shapes are
 * accepted so `usePaginated` does not need to know which is which.
 */
function normaliseItems<T>(data: T[] | { items?: T[] } | undefined): T[] {
  if (Array.isArray(data)) return data
  if (data && Array.isArray((data as { items?: T[] }).items)) return (data as { items: T[] }).items
  return []
}

function buildQuery(query: RequestOptions['query']): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    params.append(key, String(value))
  }
  const text = params.toString()
  return text ? `?${text}` : ''
}

function resolveHeaders(headers: ApiClientOptions['headers']): Record<string, string> {
  if (!headers) return {}
  return typeof headers === 'function' ? headers() : headers
}

function statusToCode(status: number): ApiErrorCode {
  switch (status) {
    case 400:
      return 'validation_failed'
    case 401:
      return 'unauthenticated'
    case 403:
      return 'forbidden'
    case 404:
      return 'not_found'
    case 409:
      return 'conflict'
    case 429:
      return 'rate_limited'
    case 503:
      return 'service_unavailable'
    default:
      return status >= 500 ? 'internal_error' : 'conflict'
  }
}

async function errorFromResponse(response: Response, requestId?: string): Promise<ApiError> {
  let message = `Request failed with HTTP ${response.status}.`
  let code = statusToCode(response.status)
  try {
    const envelope = (await response.json()) as ApiEnvelope<unknown>
    if (envelope.error) {
      code = envelope.error.code
      message = envelope.error.message
    }
  } catch {
    // A non-JSON error body (a proxy's HTML 502 page) is expected; the status
    // is enough to classify it.
  }
  return new ApiError({
    code,
    message,
    status: response.status,
    ...(requestId !== undefined ? { requestId } : {}),
  })
}

function abortOrNetworkError(cause: unknown, callerSignal: AbortSignal | undefined, url: string): ApiError {
  const aborted = cause instanceof Error && cause.name === 'AbortError'
  const timedOut = cause instanceof Error && cause.name === 'TimeoutError'

  if (timedOut || (aborted && !callerSignal?.aborted)) {
    return new ApiError({
      code: 'timeout',
      message: 'The server did not answer in time. Check the connection and try again.',
      status: 0,
      cause,
    })
  }
  if (aborted) {
    return new ApiError({ code: 'aborted', message: 'Request cancelled.', status: 0, cause })
  }
  return new ApiError({
    code: 'network_error',
    message: 'Could not reach the server. Check the connection and try again.',
    status: 0,
    details: { url },
    cause,
  })
}

/**
 * `AbortSignal.any` is not in every runtime this ships to, so the fallback
 * wires a controller by hand. Leaked listeners here would accumulate one per
 * request, hence the removal on the first fire.
 */
function combineSignals(a: AbortSignal, b?: AbortSignal): AbortSignal {
  if (!b) return a
  const anyOf = (AbortSignal as unknown as { any?: (signals: AbortSignal[]) => AbortSignal }).any
  if (typeof anyOf === 'function') return anyOf([a, b])

  const controller = new AbortController()
  const abort = (source: AbortSignal) => () => controller.abort(source.reason)
  const onA = abort(a)
  const onB = abort(b)
  if (a.aborted) controller.abort(a.reason)
  else if (b.aborted) controller.abort(b.reason)
  else {
    a.addEventListener('abort', onA, { once: true })
    b.addEventListener('abort', onB, { once: true })
  }
  return controller.signal
}

function newRequestId(): string {
  const cryptoObj = globalThis.crypto as Crypto | undefined
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') return cryptoObj.randomUUID()
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
